import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetPatientDocumentsQueryKey,
  PatientDocumentCategory,
  TemplateType,
  useGenerateDocument,
} from '../../../api/created/documents.ts';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { DOCUMENT_TEMPLATES_BY_CATEGORY, getDocumentCategoryName } from '../../../utils/document.ts';

interface FormValues {
  category: PatientDocumentCategory | null;
  templateType: TemplateType | null;
}

const defaultValues: FormValues = {
  category: null,
  templateType: null,
};

export function CreateDocumentDialog({
  patientId,
  categories,
  open,
  onClose,
}: {
  patientId: string;
  categories: readonly PatientDocumentCategory[];
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const {
    control,
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues,
  });

  const selectedCategory = watch('category');
  const templateOptions = selectedCategory ? DOCUMENT_TEMPLATES_BY_CATEGORY[selectedCategory] : [];

  const generateDocumentMutation = useGenerateDocument({
    onSuccess: () => {
      toast.success('Dokument wurde erfolgreich erstellt');
      void queryClient.invalidateQueries({ queryKey: getGetPatientDocumentsQueryKey(patientId) });
      handleOnClose();
    },
    onError: () => {
      toast.error('Beim Erstellen des Dokuments ist ein Fehler aufgetreten');
    },
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const handleOnClose = () => {
    reset(defaultValues);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 600 }}>
        <ModalClose />
        <Typography level={'h4'}>Dokument erstellen</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit(({ templateType }) => {
            if (!templateType) {
              return;
            }

            generateDocumentMutation.mutate({
              patientId,
              templateType,
            });
          })}>
          <Stack gap={2}>
            <FormControl error={!!errors.category}>
              <FormLabel>
                <Typography>Kategorie *</Typography>
              </FormLabel>
              <Controller
                name="category"
                control={control}
                rules={{ required: 'Kategorie muss ausgewählt werden' }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(_, newValue) => {
                      onChange(newValue);
                      setValue('templateType', null);
                    }}
                    placeholder="Bitte auswählen">
                    {categories.map((category) => (
                      <Option key={category} value={category}>
                        {getDocumentCategoryName(category)}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.category && <FormHelperText>{errors.category.message}</FormHelperText>}
            </FormControl>

            <FormControl error={!!errors.templateType}>
              <FormLabel>
                <Typography>Vorlagenname *</Typography>
              </FormLabel>
              <Controller
                name="templateType"
                control={control}
                rules={{ required: 'Vorlagenname muss ausgewählt werden' }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(_, newValue) => onChange(newValue)}
                    placeholder="Bitte auswählen"
                    disabled={!selectedCategory}>
                    {templateOptions.map((template) => (
                      <Option key={template.value} value={template.value}>
                        {template.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.templateType && <FormHelperText>{errors.templateType.message}</FormHelperText>}
            </FormControl>

            <FormActionButtons onAbortClick={handleOnClose} isLoading={generateDocumentMutation.isPending} />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
